import { Fragment, memo, useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, ChevronDown, ChevronRight, Download, RotateCcw, X } from "lucide-react";

import { PaginationBar } from "@/components/pagination-bar";
import { useApiHeaders } from "@/hooks/use-api-headers";
import {
  overridesQueryKey,
  overridesToCatalog,
  useI18nOverrides,
  type TranslationOverride,
} from "@/hooks/use-i18n-overrides";
import {
  enCatalog,
  flattenCatalog,
  loadCatalog,
  placeholdersMatch,
  placeholdersOf,
} from "@/i18n/catalog";
import { appLocales, defaultLocale, localeNames, type Locale } from "@/i18n/config";
import { loadProvenance } from "@/i18n/provenance";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ConfigSelect } from "@/components/config-select";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

/** English is the source language, so it has no overrides — correcting it is a commit. */
const EDITABLE_LOCALES = appLocales.filter((locale) => locale !== defaultLocale);

const LOCALE_OPTIONS = EDITABLE_LOCALES.map((locale) => ({
  value: locale,
  label: `${localeNames[locale]} (${locale})`,
}));

interface Row {
  key: string;
  namespace: string;
  english: string;
  /** What ships in the locale's catalogue. Undefined when the locale has no such key at all. */
  bundle: string | undefined;
  override: TranslationOverride | undefined;
  /** True when `translate_catalog.py` wrote this locale's value (POS2-1150). */
  byClaude: boolean;
}

/** How long a pause counts as "stopped typing". Same 200ms the labelling relabel drawer uses —
 * long enough to skip the intermediate passes, short enough that the table never feels stuck. */
const SEARCH_DEBOUNCE_MS = 200;

/** The settled value of a fast-changing input. Deliberately a local four-line hook rather than a
 * shared util: `relabel-drawer.tsx` has the same one, and two copies of four lines is cheaper than
 * a utility module two callers import (POS2-1134 CP5). */
function useDebounced(value: string, ms: number): string {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

function currentValue(row: Row): string {
  return row.override?.value ?? row.bundle ?? row.english;
}

/** A key nobody has translated: absent from the locale, or byte-identical to its English. */
function isUntranslated(row: Row): boolean {
  if (row.override) return false;
  return row.bundle === undefined || row.bundle === row.english;
}

/** Machine German nobody has read — the reviewer's queue (POS2-1150).
 *
 * An override wins, because typing a correction on this page *is* the review. `isUntranslated`
 * wins too: a value byte-identical to its English was not translated whatever the mark says, and
 * calling that a machine translation would put a row in the queue with nothing to review.
 */
function isUnreviewedMachine(row: Row): boolean {
  return row.byClaude && !row.override && !isUntranslated(row);
}

/**
 * Every key sharing one English string (POS2-1175).
 *
 * 127 English strings repeat across 304 keys, so correcting the catalogue by key means typing the
 * same German up to nine times — and POS2-1150's reflux caught two corrections that had been typed
 * twice and two that had not. The group is the unit of review; the key stays the unit of storage,
 * because a key is the string's *location* and identical English legitimately needs different
 * German where the location changes its grammar.
 *
 * Most groups hold one key and render exactly as a row always has. `english` identifies a group:
 * groups are defined by distinct English, so it is unique by construction.
 */
interface Group {
  english: string;
  rows: Row[];
  /** The distinct values across the group. More than one is the divergence check 5 gates. */
  values: string[];
}

/**
 * What is open in the inline editor: a whole group, or one key inside an expanded group.
 *
 * A group and its members are edited through the same cell, so a single `editing` key would open
 * every member's editor at once the moment a group was being edited.
 */
type Editing = { scope: "group"; english: string } | { scope: "key"; key: string };

/** The badge a row shows — and, compared across a group, what decides whether its badges agree. */
function sourceOf(row: Row): string {
  if (row.override) return row.override.source;
  if (isUntranslated(row)) return "untranslated";
  return row.byClaude ? "Claude" : "reviewed";
}

function SourceBadge({ source }: { source: string }) {
  if (source === "untranslated") {
    return (
      <Badge variant="outline" className="text-orange-700">
        untranslated
      </Badge>
    );
  }
  if (source === "Claude") {
    return (
      <Badge variant="outline" className="text-blue-700">
        Claude
      </Badge>
    );
  }
  if (source === "reviewed") return <Badge variant="outline">reviewed</Badge>;
  // An override carries its own `source` from the database — `human`, `claude`, whatever wrote it.
  return <Badge variant="secondary">{source}</Badge>;
}

/**
 * The platform-admin Internationalisation table (POS2-1133).
 *
 * **English-only, on purpose** — a staff surface, exempt from the i18n sweep along with the other
 * `/admin/*` pages, so nothing here goes through `t()`.
 *
 * The table is assembled in the browser: the English catalogue is in the entry chunk, the locale's
 * catalogue is its own lazily-loaded chunk, and the API supplies only the overrides. The backend
 * never joins the three, which is why there is no copy of the catalogues in the Python package to
 * drift out of date.
 */
export default function AdminTranslationsPage() {
  const headers = useApiHeaders();
  const queryClient = useQueryClient();

  const [locale, setLocale] = useState<Locale>(EDITABLE_LOCALES[0]);
  const [namespace, setNamespace] = useState("all");
  const [search, setSearch] = useState("");
  const [untranslatedOnly, setUntranslatedOnly] = useState(false);
  const [editedOnly, setEditedOnly] = useState(false);
  const [machineOnly, setMachineOnly] = useState(false);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);

  // "all", not the runtime default: this table is where a reviewer sees what a bulk import
  // wrote, so it must show `claude` rows the runtime deliberately skips (POS2-1134).
  const overrides = useI18nOverrides(locale, "all");
  const bundle = useQuery({
    queryKey: ["i18n-bundle", locale],
    // The catalogue is a build asset: once fetched it cannot change without a deploy, which is
    // also what invalidates the chunk's hashed URL.
    staleTime: Infinity,
    queryFn: async () => {
      const catalog = await loadCatalog(locale);
      return catalog ? flattenCatalog(catalog) : {};
    },
  });

  // Same build-asset reasoning as the bundle above. This is the only importer of `provenance.ts`,
  // and `manualChunks` keeps the dotfile out of the precached locale chunk — both halves are
  // needed for the map to travel with this page rather than with every user (POS2-1150).
  const provenance = useQuery({
    queryKey: ["i18n-provenance", locale],
    staleTime: Infinity,
    queryFn: () => loadProvenance(locale),
  });

  const english = useMemo(() => flattenCatalog(enCatalog), []);

  const rows = useMemo<Row[]>(() => {
    const byKey = new Map<string, TranslationOverride>(
      (overrides.data ?? []).map((o) => [`${o.namespace}.${o.key}`, o]),
    );
    return Object.entries(english)
      .map(([key, value]) => ({
        key,
        namespace: key.split(".")[0],
        english: value,
        bundle: bundle.data?.[key],
        override: byKey.get(key),
        byClaude: provenance.data?.has(key) ?? false,
      }))
      .sort((a, b) => a.key.localeCompare(b.key));
  }, [english, bundle.data, overrides.data, provenance.data]);

  const namespaceOptions = useMemo(
    () => [
      { value: "all", label: "All namespaces" },
      ...[...new Set(rows.map((row) => row.namespace))]
        .sort()
        .map((ns) => ({ value: ns, label: ns })),
    ],
    [rows],
  );

  // The filter reads the *settled* search, not the typed one: three `toLowerCase()` per row
  // against the key, the English and the current value is a full pass over the catalogue, and at
  // this ticket's final size that ran on every keystroke. Debouncing moves it to one pass per
  // pause. The input stays controlled by `search`, so typing is still immediate (POS2-1134 CP5).
  const settledSearch = useDebounced(search, SEARCH_DEBOUNCE_MS);

  // `rows` is sorted by key, so a Map keyed on English yields each group's members in key order
  // and the groups themselves in first-key order — the table keeps the ordering it always had.
  const groups = useMemo<Group[]>(() => {
    const byEnglish = new Map<string, Row[]>();
    for (const row of rows) {
      const existing = byEnglish.get(row.english);
      if (existing) existing.push(row);
      else byEnglish.set(row.english, [row]);
    }
    return [...byEnglish.values()].map((members) => ({
      english: members[0].english,
      rows: members,
      values: [...new Set(members.map(currentValue))],
    }));
  }, [rows]);

  // A filter keeps a group when *any* member matches, because the group is one action: a group
  // holding one unreviewed key is work, and editing it clears that key along with its siblings.
  const visible = useMemo(() => {
    const needle = settledSearch.trim().toLowerCase();
    const matches = (row: Row) => {
      if (namespace !== "all" && row.namespace !== namespace) return false;
      // A union, not an intersection (POS2-1186). ANDed, "Edited only" + "Machine translation
      // only" could never return anything — one requires an override and the other its absence —
      // so the pair read as a broken page rather than as a contradiction. The labels promise
      // "show me these", and someone ticking two is asking to see both sets.
      const flagged = untranslatedOnly || editedOnly || machineOnly;
      if (
        flagged &&
        !(
          (untranslatedOnly && isUntranslated(row)) ||
          (editedOnly && row.override) ||
          (machineOnly && isUnreviewedMachine(row))
        )
      ) {
        return false;
      }
      if (!needle) return true;
      return (
        row.key.toLowerCase().includes(needle) ||
        row.english.toLowerCase().includes(needle) ||
        currentValue(row).toLowerCase().includes(needle)
      );
    };
    return groups.filter((group) => group.rows.some(matches));
  }, [groups, namespace, settledSearch, untranslatedOnly, editedOnly, machineOnly]);

  /**
   * Paginated, not rendered whole (POS2-1134 CP3a).
   *
   * The table used to map every key straight into the DOM, each row carrying an editable cell.
   * That was free at the ~60 keys POS2-1133 shipped; this ticket takes the catalogue to several
   * hundred, and the mount cost plus a re-render per keystroke made the one screen whose purpose
   * is finding a key among many the slowest in the app. Pagination is the cheap half of the fix
   * the ticket asks for — windowing would keep a single scroll, but a page of 50 with the search
   * and namespace filters above it is how someone actually finds a key here.
   */
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  // A page of groups, not of keys (POS2-1175). An expanded group's members render under it rather
  // than displacing other groups, so a page holds the same number of decisions either way.
  const pageGroups = useMemo(
    () => visible.slice(page * pageSize, (page + 1) * pageSize),
    [visible, page, pageSize],
  );

  /** What the reader currently has on, in the words the checkboxes use. */
  const activeFilters = useMemo(() => {
    const names: string[] = [];
    if (namespace !== "all") names.push(`namespace ${namespace}`);
    if (settledSearch) names.push(`search "${settledSearch}"`);
    if (untranslatedOnly) names.push("Untranslated only");
    if (editedOnly) names.push("Edited only");
    if (machineOnly) names.push("Machine translation only");
    return names;
  }, [namespace, settledSearch, untranslatedOnly, editedOnly, machineOnly]);

  const resetFilters = useCallback(() => {
    setNamespace("all");
    setSearch("");
    setUntranslatedOnly(false);
    setEditedOnly(false);
    setMachineOnly(false);
  }, []);

  // Any filter change re-shapes the list under the cursor, so page 3 of the old result set is
  // meaningless against the new one.
  useEffect(
    () => setPage(0),
    [namespace, settledSearch, untranslatedOnly, editedOnly, machineOnly, locale],
  );

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: overridesQueryKey(locale, "all") });

  async function putOverride(row: Row, value: string) {
    const [ns, ...rest] = row.key.split(".");
    const res = await fetch(`${API_URL}/api/v1/i18n/overrides/${locale}/${ns}/${rest.join(".")}`, {
      method: "PUT",
      headers,
      body: JSON.stringify({ value, base_en: row.english }),
    });
    if (!res.ok) throw new Error(`Failed to save the override (${res.status})`);
  }

  /**
   * One write path for a key and for a group, because a group of one *is* a key (POS2-1175).
   *
   * The endpoint is per-key, so a group is N requests and a partial failure is possible. They are
   * all issued and then reported honestly, naming the keys that did not save: all-or-nothing would
   * need either a bulk route or a rollback that can itself fail halfway, and would throw away
   * writes that did succeed. A partial save is visible and recoverable — the group re-renders as a
   * conflict with the failed keys still on their old value, which is exactly the retry list.
   */
  const save = useMutation({
    mutationFn: async ({ rows: targets, value }: { rows: Row[]; value: string }) => {
      const results = await Promise.allSettled(targets.map((row) => putOverride(row, value)));
      const failed = targets
        .filter((_, i) => results[i].status === "rejected")
        .map((row) => row.key);
      return { saved: targets.length - failed.length, failed };
    },
    onSuccess: ({ saved, failed }) => {
      invalidate();
      setEditing(null);
      if (failed.length) {
        toast.error(
          `Saved ${saved} of ${saved + failed.length} — these did not save: ${failed.join(", ")}`,
        );
        return;
      }
      toast.success(
        saved === 1
          ? "Translation saved — other users see it within 5 minutes"
          : `Saved ${saved} keys — other users see it within 5 minutes`,
      );
    },
    onError: () => toast.error("Couldn't save the translation"),
  });

  const revert = useMutation({
    mutationFn: async (row: Row) => {
      const [ns, ...rest] = row.key.split(".");
      const res = await fetch(
        `${API_URL}/api/v1/i18n/overrides/${locale}/${ns}/${rest.join(".")}`,
        { method: "DELETE", headers },
      );
      if (!res.ok) throw new Error(`Failed to revert the override (${res.status})`);
    },
    onSuccess: () => {
      invalidate();
      toast.success("Reverted to the bundled translation");
    },
    onError: () => toast.error("Couldn't revert the translation"),
  });

  /**
   * Revert every overridden key in a group (POS2-1186).
   *
   * Per-key requests with a partial failure naming the keys that did not go, which is the shape
   * POS2-1175 chose for a group *save* and for the same reason: the endpoint is per-key, and
   * all-or-nothing would need a compensating write that can itself fail halfway and would discard
   * the reverts that succeeded. Keys with no override are skipped rather than sent — a DELETE on
   * nothing is a 404 the reader would have to interpret.
   */
  const revertGroup = useMutation({
    mutationFn: async (group: Group) => {
      const overridden = group.rows.filter((row) => row.override);
      const failed: string[] = [];
      for (const row of overridden) {
        const [ns, ...rest] = row.key.split(".");
        const res = await fetch(
          `${API_URL}/api/v1/i18n/overrides/${locale}/${ns}/${rest.join(".")}`,
          { method: "DELETE", headers },
        );
        if (!res.ok) failed.push(row.key);
      }
      return { attempted: overridden.length, failed };
    },
    onSuccess: ({ attempted, failed }) => {
      invalidate();
      if (failed.length === 0) {
        toast.success(`Reverted ${attempted} key${attempted === 1 ? "" : "s"}`);
        return;
      }
      toast.error(`Could not revert ${failed.length} of ${attempted}`, {
        description: failed.join(", "),
      });
    },
    onError: () => toast.error("Couldn't revert the group"),
  });

  const cancelEditing = useCallback(() => setEditing(null), []);
  const revertRow = useCallback((row: Row) => revert.mutate(row), [revert]);
  const revertRows = useCallback((group: Group) => revertGroup.mutate(group), [revertGroup]);
  const toggleExpanded = useCallback((english: string) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (!next.delete(english)) next.add(english);
      return next;
    });
  }, []);

  function commitRow(row: Row) {
    commit([row]);
  }

  function commitGroup(group: Group) {
    commit(group.rows);
  }

  function startEditingRow(row: Row) {
    setEditing({ scope: "key", key: row.key });
    setDraft(currentValue(row));
    setError(null);
  }

  function startEditingGroup(group: Group) {
    setEditing({ scope: "group", english: group.english });
    // A group whose members disagree has no single current value to seed, so the reviewer starts
    // from the first and the conflict list beside it offers the others in one click.
    setDraft(group.values[0]);
    setError(null);
  }

  /**
   * Writes `draft` to every row given — one for a key, all of them for a group.
   *
   * Every member's placeholders are checked, not just the first. They share their English and so
   * should agree, but a bundle that does not is exactly the case worth refusing by name rather
   * than writing a value that breaks one screen's interpolation.
   */
  function commit(targets: Row[]) {
    const value = draft.trim();
    if (!value) {
      setError("A translation cannot be empty — use Revert to go back to the bundled value.");
      return;
    }
    for (const row of targets) {
      // The bundled value is the contract: it is what the code interpolates against. Comparing to
      // the *English* instead would reject a legitimate German whose bundle already differs.
      const reference = row.bundle ?? row.english;
      if (placeholdersMatch(reference, value)) continue;
      const wanted = placeholdersOf(reference);
      setError(
        wanted.length
          ? `Keep the same placeholders as ${row.key}: ${wanted.join(", ")}`
          : `${row.key} has no placeholders, so this value cannot add any.`,
      );
      return;
    }
    setError(null);
    save.mutate({ rows: targets, value });
  }

  const overrideCount = overrides.data?.length ?? 0;
  const conflictCount = groups.filter((group) => group.values.length > 1).length;

  /** The corrections as catalogue-shaped JSON, for folding back into `messages/<locale>/` in a
   * commit. Deliberately a manual download rather than anything automated: a job that wrote to the
   * repository — or read the repository into this table — would make the catalogue and the database
   * two sources of truth for the same string. */
  function downloadOverrides() {
    const catalog = overridesToCatalog(overrides.data ?? []);
    const blob = new Blob([JSON.stringify(catalog, null, 2) + "\n"], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `translation-overrides-${locale}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-6">
      {/* Title left, action top-right — the header shape `admin/labelling.tsx:288` uses and the
          app's current convention (POS2-1186). The download previously sat in the filter row with
          `ml-auto` inside a `flex-wrap` container, so it right-aligned within that row while the
          row fitted and wrapped onto a line of its own once it did not: two correct layouts the
          page crossed between on a resize, moving the table each time. */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="font-display text-xl font-extrabold tracking-tight text-gray-900">
            Internationalisation
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Every key in the English catalogue, with what the selected locale renders for it.
            Editing a value stores an override that takes effect without a deploy;{" "}
            <span className="font-medium">other users see a change within 5 minutes</span>. Revert
            removes the override and falls back to the value shipped in the build.
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {overrideCount} override{overrideCount === 1 ? "" : "s"} in {locale} · {rows.length}{" "}
            keys in {groups.length} groups
            {conflictCount > 0 && ` · ${conflictCount} with conflicting translations`}
            {overrides.isError && " · could not load overrides — showing bundled values only"}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="shrink-0"
          disabled={overrideCount === 0}
          onClick={downloadOverrides}
        >
          <Download className="size-4" />
          Download overrides
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-48">
          <label className="text-xs font-medium text-muted-foreground" htmlFor="locale">
            Locale
          </label>
          <ConfigSelect
            id="locale"
            options={LOCALE_OPTIONS}
            value={locale}
            onChange={(e) => {
              setLocale(e.target.value as Locale);
              setEditing(null);
              setExpanded(new Set());
            }}
          />
        </div>
        <div className="w-48">
          <label className="text-xs font-medium text-muted-foreground" htmlFor="namespace">
            Namespace
          </label>
          <ConfigSelect
            id="namespace"
            options={namespaceOptions}
            value={namespace}
            onChange={(e) => setNamespace(e.target.value)}
          />
        </div>
        <div className="w-64">
          <label className="text-xs font-medium text-muted-foreground" htmlFor="search">
            Search
          </label>
          <Input
            id="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="key or text"
          />
        </div>
        <label className="flex items-center gap-2 pb-2 text-sm" htmlFor="untranslated-only">
          <Checkbox
            id="untranslated-only"
            checked={untranslatedOnly}
            onCheckedChange={(v) => setUntranslatedOnly(v === true)}
          />
          Untranslated only
        </label>
        <label className="flex items-center gap-2 pb-2 text-sm" htmlFor="edited-only">
          <Checkbox
            id="edited-only"
            checked={editedOnly}
            onCheckedChange={(v) => setEditedOnly(v === true)}
          />
          Edited only
        </label>
        {/* The deliverable of POS2-1150: the list of German a model wrote and nobody has read.
            It drains itself — correcting a row here makes it `edited`, which drops it out.

            Named for what it means rather than for the vendor (POS2-1186). The two *stored* values
            behind it deliberately still say `claude`: `by` in `.source-hashes.json`, written by
            `translate_catalog.py`, and `source` in the `translation_overrides` CHECK, whose
            migration is immutable. Renaming those is not a rename, it is a migration. */}
        <label className="flex items-center gap-2 pb-2 text-sm" htmlFor="machine-only">
          <Checkbox
            id="machine-only"
            checked={machineOnly}
            onCheckedChange={(v) => setMachineOnly(v === true)}
          />
          Machine translation only
        </label>
      </div>

      {bundle.isLoading ? (
        <div className="space-y-2">
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[22%]">Key</TableHead>
              <TableHead className="w-[28%]">English</TableHead>
              <TableHead>{localeNames[locale]}</TableHead>
              <TableHead className="w-24">Source</TableHead>
              <TableHead className="w-28" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageGroups.map((group) => {
              // A group of one is a key, and renders exactly as a row always has — no group
              // chrome for the ~1,250 strings that appear under a single key.
              if (group.rows.length === 1) {
                const row = group.rows[0];
                return (
                  <TranslationRow
                    key={row.key}
                    row={row}
                    isEditing={editing?.scope === "key" && editing.key === row.key}
                    draft={draft}
                    error={error}
                    isSaving={save.isPending}
                    isReverting={revert.isPending}
                    onStartEditing={startEditingRow}
                    onDraftChange={setDraft}
                    onCommit={commitRow}
                    onCancel={cancelEditing}
                    onRevert={revertRow}
                  />
                );
              }
              const isOpen = expanded.has(group.english);
              return (
                <Fragment key={group.english}>
                  <GroupRow
                    group={group}
                    isOpen={isOpen}
                    isEditing={editing?.scope === "group" && editing.english === group.english}
                    draft={draft}
                    error={error}
                    isSaving={save.isPending}
                    onToggle={toggleExpanded}
                    onStartEditing={startEditingGroup}
                    onDraftChange={setDraft}
                    onCommit={commitGroup}
                    onCancel={cancelEditing}
                    onRevert={revertRows}
                    isReverting={revertGroup.isPending}
                  />
                  {isOpen &&
                    group.rows.map((row) => (
                      <TranslationRow
                        key={row.key}
                        row={row}
                        inGroup
                        isEditing={editing?.scope === "key" && editing.key === row.key}
                        draft={draft}
                        error={error}
                        isSaving={save.isPending}
                        isReverting={revert.isPending}
                        onStartEditing={startEditingRow}
                        onDraftChange={setDraft}
                        onCommit={commitRow}
                        onCancel={cancelEditing}
                        onRevert={revertRow}
                      />
                    ))}
                </Fragment>
              );
            })}
            {visible.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">
                  {/* Names what is on and offers the way out (POS2-1186). Under the union
                      semantics a blank table is always a real result rather than a contradiction,
                      so saying which filters produced it is the whole of the explanation. */}
                  No keys match {activeFilters.length ? activeFilters.join(" + ") : "these filters"}
                  .
                  {activeFilters.length > 0 && (
                    <Button
                      variant="link"
                      size="sm"
                      className="ml-1 h-auto p-0"
                      onClick={resetFilters}
                    >
                      Reset filters
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
      {visible.length > 0 && (
        <PaginationBar
          page={page}
          pageSize={pageSize}
          total={visible.length}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(0);
          }}
        />
      )}
    </div>
  );
}

/**
 * The header row for a group of keys sharing one English string (POS2-1175).
 *
 * Not memoised, unlike `TranslationRow` — and deliberately, rather than by omission. The memo
 * there does not currently hold either, because `onCommit` and `onStartEditing` are declared in
 * the page body and change identity every render; adding a second memo that never hits would look
 * like a working optimisation. See the note in BRANCH.md.
 */
function GroupRow({
  group,
  isOpen,
  isEditing,
  draft,
  error,
  isSaving,
  onToggle,
  onStartEditing,
  onDraftChange,
  onCommit,
  onCancel,
  onRevert,
  isReverting,
}: {
  group: Group;
  isOpen: boolean;
  isEditing: boolean;
  draft: string;
  error: string | null;
  isSaving: boolean;
  onToggle: (english: string) => void;
  onStartEditing: (group: Group) => void;
  onDraftChange: (value: string) => void;
  onCommit: (group: Group) => void;
  onCancel: () => void;
  onRevert: (group: Group) => void;
  isReverting: boolean;
}) {
  const sources = [...new Set(group.rows.map(sourceOf))];
  const conflicted = group.values.length > 1;
  const overriddenCount = group.rows.filter((row) => row.override).length;

  return (
    <TableRow className="bg-muted/40">
      <TableCell className="align-top">
        <button
          type="button"
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          aria-expanded={isOpen}
          aria-label={`${isOpen ? "Collapse" : "Expand"} the ${group.rows.length} keys for "${group.english}"`}
          onClick={() => onToggle(group.english)}
        >
          {isOpen ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
          {group.rows.length} places
        </button>
        {/* The keys are listed collapsed, not hidden behind the expansion, because *which* keys
            share the string is the judgement the group asks for — one German has to fit a sidebar
            item and a table column, and the namespaces are how a reviewer sees that. The largest
            group in the catalogue is nine keys. Expanded, each member prints its own key, so
            repeating the list there would be noise. */}
        {!isOpen && (
          <div className="mt-1 space-y-0.5 font-mono text-xs text-muted-foreground">
            {group.rows.map((row) => (
              <div key={row.key}>{row.key}</div>
            ))}
          </div>
        )}
      </TableCell>
      <TableCell className="align-top text-sm whitespace-normal">{group.english}</TableCell>
      <TableCell className="align-top text-sm whitespace-normal">
        {isEditing ? (
          <div className="space-y-1">
            <Input
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              aria-label={`Translation for all ${group.rows.length} keys with "${group.english}"`}
              value={draft}
              onChange={(e) => onDraftChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") onCommit(group);
                if (e.key === "Escape") onCancel();
              }}
            />
            {/* Picking one of the values already in the group is the repair path for a conflict —
                the reviewer chooses which of the two is right rather than retyping it. */}
            {conflicted && (
              <div className="flex flex-wrap items-center gap-1">
                {group.values.map((value) => (
                  <Button
                    key={value}
                    size="sm"
                    variant="outline"
                    className="h-6 px-2 text-xs"
                    onClick={() => onDraftChange(value)}
                  >
                    {value}
                  </Button>
                ))}
              </div>
            )}
            {error && <p className="text-xs text-red-600">{error}</p>}
          </div>
        ) : (
          <button
            type="button"
            className="w-full cursor-text text-left hover:bg-muted"
            onClick={() => onStartEditing(group)}
          >
            {conflicted ? (
              <span className="text-orange-700">{group.values.join("  ·  ")}</span>
            ) : (
              group.values[0]
            )}
          </button>
        )}
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-1">
          {/* One badge when the group agrees; `mixed` when it does not, because a collapsed row
              cannot honestly claim one of several states (POS2-1175). The per-key badges are in
              the expansion. */}
          {sources.length === 1 ? (
            <SourceBadge source={sources[0]} />
          ) : (
            <Badge variant="outline" className="text-muted-foreground" title={sources.join(", ")}>
              mixed
            </Badge>
          )}
          {conflicted && (
            <Badge variant="outline" className="text-orange-700">
              conflict
            </Badge>
          )}
        </div>
      </TableCell>
      <TableCell className="text-right">
        {isEditing && (
          <div className="flex justify-end gap-1">
            <Button
              size="icon"
              variant="ghost"
              className="size-7"
              aria-label={`Save all ${group.rows.length} keys with "${group.english}"`}
              disabled={isSaving}
              onClick={() => onCommit(group)}
            >
              <Check className="size-4" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="size-7"
              aria-label={`Cancel editing "${group.english}"`}
              onClick={onCancel}
            >
              <X className="size-4" />
            </Button>
          </div>
        )}
        {/* A collapsed group had no revert at all, so undoing five overridden keys meant expanding
            it and clicking five times (POS2-1186). Shown only when something is actually
            overridden — a revert on a group with none would be a no-op the reader has to
            interpret. */}
        {!isEditing && overriddenCount > 0 && (
          <Button
            size="icon"
            variant="ghost"
            className="size-7"
            aria-label={`Revert ${overriddenCount} overridden key${overriddenCount === 1 ? "" : "s"} with "${group.english}"`}
            title={`Revert ${overriddenCount} of ${group.rows.length}`}
            disabled={isReverting}
            onClick={() => onRevert(group)}
          >
            <RotateCcw className="size-4" />
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}

/**
 * One row, memoised (POS2-1134 CP3a).
 *
 * Every keystroke in the inline editor moves `draft`, which lives on the page — so without this
 * the whole table re-renders per character. That was free at the ~60 keys POS2-1133 shipped and
 * stops being free as this ticket fills the catalogue: at 333 keys a twelve-character edit
 * already timed the admin page's own test out at five seconds.
 *
 * Memoising the row means a keystroke re-renders the row being edited and nothing else. The
 * mount cost of a few hundred rows, and the per-keystroke cost of the *search* box, are the other
 * half of the same problem and are CP5's, where the catalogue reaches its final size — this is
 * the part that had to move because it broke the build.
 *
 * The callbacks take `row` rather than closing over it, so the page can hand down stable
 * references; a `() => commit(row)` per row would defeat the memo entirely.
 */
const TranslationRow = memo(function TranslationRow({
  row,
  inGroup = false,
  isEditing,
  draft,
  error,
  isSaving,
  isReverting,
  onStartEditing,
  onDraftChange,
  onCommit,
  onCancel,
  onRevert,
}: {
  row: Row;
  /** Rendered inside an expanded group, so the key cell is indented under its group's header. */
  inGroup?: boolean;
  isEditing: boolean;
  draft: string;
  error: string | null;
  isSaving: boolean;
  isReverting: boolean;
  onStartEditing: (row: Row) => void;
  onDraftChange: (value: string) => void;
  onCommit: (row: Row) => void;
  onCancel: () => void;
  onRevert: (row: Row) => void;
}) {
  return (
    <TableRow>
      <TableCell className={`font-mono text-xs text-muted-foreground${inGroup ? " pl-8" : ""}`}>
        {row.key}
      </TableCell>
      <TableCell className="align-top text-sm whitespace-normal">{row.english}</TableCell>
      <TableCell className="align-top text-sm whitespace-normal">
        {isEditing ? (
          <div className="space-y-1">
            <Input
              // Focus on open is the point of an inline editor: the row was clicked to start
              // typing in it, and a second click to reach the field would be the usability
              // problem this rule usually prevents.
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              aria-label={`Translation for ${row.key}`}
              value={draft}
              onChange={(e) => onDraftChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") onCommit(row);
                if (e.key === "Escape") onCancel();
              }}
            />
            {error && <p className="text-xs text-red-600">{error}</p>}
          </div>
        ) : (
          <button
            type="button"
            className="w-full cursor-text text-left hover:bg-muted"
            onClick={() => onStartEditing(row)}
          >
            {currentValue(row)}
          </button>
        )}
      </TableCell>
      {/* Four states now (POS2-1150). POS2-1133's PRD asked for `bundle / Claude / edited` and CP4
          could only ship three, because the translation script writes checked-in catalogue files
          and never the database — so machine German arrives *as* the bundle and read `bundle`.
          Provenance comes from the script's own hash file instead, which is what makes `Claude`
          reachable and splits the old `bundle` into `Claude` and `reviewed`.

          `Claude` means "not corrected through this page", not "definitely unread": hand-editing
          `messages/de/*.json` in a commit does not clear the mark, because only the script
          maintains it. The mark self-corrects on the next run, which rebuilds it from what was
          actually sent. The header says so, rather than the table implying more than it knows. */}
      <TableCell>
        <SourceBadge source={sourceOf(row)} />
      </TableCell>
      <TableCell className="text-right">
        {isEditing ? (
          <div className="flex justify-end gap-1">
            <Button
              size="icon"
              variant="ghost"
              className="size-7"
              aria-label={`Save ${row.key}`}
              disabled={isSaving}
              onClick={() => onCommit(row)}
            >
              <Check className="size-4" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="size-7"
              aria-label={`Cancel editing ${row.key}`}
              onClick={onCancel}
            >
              <X className="size-4" />
            </Button>
          </div>
        ) : (
          row.override && (
            <Button
              size="icon"
              variant="ghost"
              className="size-7"
              aria-label={`Revert ${row.key}`}
              disabled={isReverting}
              onClick={() => onRevert(row)}
            >
              <RotateCcw className="size-4" />
            </Button>
          )
        )}
      </TableCell>
    </TableRow>
  );
});
